"use client"

import { useState, useMemo, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { 
  Search, 
  MapPin, 
  Building2, 
  ScanLine,
  Calendar,
  Clock,
  Phone,
  ArrowRight,
  ArrowLeft,
  Stethoscope,
  Globe,
  ChevronLeft,
  ChevronRight,
  User,
  UserPlus,
  X
} from "lucide-react"
import { format, addDays } from "date-fns"

const locations = [
  { id: "madinaguda", name: "Madinaguda" },
  { id: "nallagandla", name: "Nallagandla" },
  { id: "isnapur", name: "Isnapur" },
  { id: "zaheerabad", name: "Zaheerabad" },
]

const specialities = [
  { id: "cardiology", name: "Cardiology", keywords: ["heart", "chest pain", "cardiac", "blood pressure", "hypertension", "heart attack", "palpitations", "angina", "coronary", "ecg", "echo"] },
  { id: "neurology", name: "Neurology", keywords: ["brain", "headache", "migraine", "stroke", "seizure", "epilepsy", "nerve", "numbness", "paralysis", "memory loss", "vertigo", "dizziness"] },
  { id: "orthopedics", name: "Orthopedics", keywords: ["bone", "joint pain", "fracture", "arthritis", "knee pain", "back pain", "spine", "shoulder pain", "sports injury", "hip replacement"] },
  { id: "paediatrics", name: "Paediatrics", keywords: ["child", "baby", "infant", "kids", "newborn", "vaccination", "growth", "fever", "child care", "pediatric"] },
  { id: "gynaecology", name: "Obstetrics & Gynaecology", keywords: ["pregnancy", "women", "menstrual", "periods", "fertility", "delivery", "prenatal", "uterus", "ovary", "pcos", "maternity"] },
  { id: "general-medicine", name: "General Medicine", keywords: ["fever", "cold", "cough", "infection", "diabetes", "thyroid", "general checkup", "viral", "flu", "weakness", "fatigue"] },
  { id: "gastroenterology", name: "Gastroenterology", keywords: ["stomach", "digestion", "acidity", "liver", "abdominal pain", "constipation", "diarrhea", "ulcer", "gastric", "bloating", "nausea"] },
  { id: "pulmonology", name: "Pulmonology", keywords: ["lung", "breathing", "asthma", "respiratory", "chest congestion", "bronchitis", "pneumonia", "sleep apnea", "copd", "wheezing"] },
  { id: "dermatology", name: "Dermatology", keywords: ["skin", "hair", "acne", "rash", "allergy", "eczema", "psoriasis", "hair loss", "pigmentation", "fungal", "itching"] },
  { id: "ent", name: "ENT", keywords: ["ear", "nose", "throat", "hearing", "sinus", "tonsils", "snoring", "ear pain", "nasal", "voice", "adenoids"] },
]

const departments = [
  { id: "dept-1", name: "Cardiology" },
  { id: "dept-2", name: "Neurology" },
  { id: "dept-3", name: "Orthopedics" },
  { id: "dept-4", name: "Obstetrics & Gynaecology" },
  { id: "dept-5", name: "Paediatrics" },
  { id: "dept-6", name: "Dermatology" },
  { id: "dept-7", name: "ENT" },
  { id: "dept-8", name: "Ophthalmology" },
  { id: "dept-9", name: "General Medicine" },
  { id: "dept-10", name: "Anesthesiology" },
  { id: "dept-11", name: "Biochemistry" },
  { id: "dept-12", name: "Critical Care" },
  { id: "dept-13", name: "Dentistry" },
  { id: "dept-14", name: "Dietetics" },
  { id: "dept-15", name: "Emergency Medicine" },
  { id: "dept-16", name: "Endocrinology" },
  { id: "dept-17", name: "Gastroenterology" },
]

// Doctor data with locations and keywords
const doctorsData = [
  {
    id: "doc-1",
    name: "Dr. Manish Gour",
    specialty: "Paediatrics",
    specialtyId: "paediatrics",
    designation: "Chief Consultant Paediatrician",
    qualifications: "MBBS, MD, DCH",
    languages: ["English", "Hindi", "Telugu"],
    locations: ["madinaguda", "nallagandla"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-manish-gour",
    experience: "15+ years",
    keywords: ["child", "baby", "infant", "kids", "newborn", "vaccination", "fever", "child care"],
  },
  {
    id: "doc-2",
    name: "Dr. Praggya Srivastava",
    specialty: "Obstetrics & Gynaecology",
    specialtyId: "gynaecology",
    designation: "Chief Consultant Obstetrician & Gynaecologist",
    qualifications: "MBBS, MS, DNB",
    languages: ["English", "Hindi"],
    locations: ["madinaguda"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-praggya-srivastava",
    experience: "18+ years",
    keywords: ["pregnancy", "women", "menstrual", "fertility", "delivery", "prenatal", "maternity"],
  },
  {
    id: "doc-3",
    name: "Dr. Jayasri Karem",
    specialty: "Obstetrics & Gynaecology",
    specialtyId: "gynaecology",
    designation: "Consultant Obstetrician & Gynaecologist",
    qualifications: "MBBS, DGO",
    languages: ["English", "Telugu"],
    locations: ["nallagandla", "isnapur"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-jayasri-karem",
    experience: "12+ years",
    keywords: ["pregnancy", "women", "periods", "pcos", "uterus", "ovary"],
  },
  {
    id: "doc-4",
    name: "Dr. Krishna Prasad",
    specialty: "General Medicine",
    specialtyId: "general-medicine",
    designation: "Chief Consultant Physician",
    qualifications: "MBBS, MD",
    languages: ["English", "Telugu", "Hindi"],
    locations: ["madinaguda", "zaheerabad"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-krishna-prasad",
    experience: "20+ years",
    keywords: ["fever", "cold", "cough", "diabetes", "thyroid", "general checkup", "infection", "pain"],
  },
  {
    id: "doc-5",
    name: "Dr. Priya Sharma",
    specialty: "Cardiology",
    specialtyId: "cardiology",
    designation: "Consultant Cardiologist",
    qualifications: "MBBS, MD, DM Cardiology",
    languages: ["English", "Hindi"],
    locations: ["madinaguda"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-priya-sharma",
    experience: "10+ years",
    keywords: ["heart", "chest pain", "cardiac", "blood pressure", "hypertension", "palpitations", "angina", "ecg"],
  },
  {
    id: "doc-6",
    name: "Dr. Vikram Singh",
    specialty: "Orthopedics",
    specialtyId: "orthopedics",
    designation: "Senior Consultant Orthopedic Surgeon",
    qualifications: "MBBS, MS Ortho",
    languages: ["English", "Hindi", "Telugu"],
    locations: ["madinaguda", "nallagandla"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-vikram-singh",
    experience: "15+ years",
    keywords: ["bone", "joint pain", "fracture", "knee pain", "back pain", "spine", "shoulder pain", "sports injury"],
  },
  {
    id: "doc-7",
    name: "Dr. Arun Kumar",
    specialty: "Neurology",
    specialtyId: "neurology",
    designation: "Consultant Neurologist",
    qualifications: "MBBS, MD, DM Neurology",
    languages: ["English", "Telugu"],
    locations: ["madinaguda"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-arun-kumar",
    experience: "12+ years",
    keywords: ["brain", "headache", "migraine", "stroke", "seizure", "nerve", "numbness", "paralysis", "vertigo"],
  },
  {
    id: "doc-8",
    name: "Dr. Ravi Kiran",
    specialty: "Gastroenterology",
    specialtyId: "gastroenterology",
    designation: "Consultant Gastroenterologist",
    qualifications: "MBBS, MD, DM Gastro",
    languages: ["English", "Telugu"],
    locations: ["nallagandla", "zaheerabad"],
    image: "/placeholder.svg?height=300&width=300",
    slug: "dr-ravi-kiran",
    experience: "14+ years",
    keywords: ["stomach", "digestion", "acidity", "liver", "abdominal pain", "constipation", "ulcer", "gastric"],
  },
  ]

// Time slots
const timeSlots = [
  { id: "slot-1", time: "9:00 AM", available: true },
  { id: "slot-2", time: "9:30 AM", available: true },
  { id: "slot-3", time: "10:00 AM", available: false },
  { id: "slot-4", time: "10:30 AM", available: true },
  { id: "slot-5", time: "11:00 AM", available: true },
  { id: "slot-6", time: "11:30 AM", available: false },
  { id: "slot-7", time: "12:00 PM", available: true },
  { id: "slot-8", time: "2:00 PM", available: true },
  { id: "slot-9", time: "2:30 PM", available: true },
  { id: "slot-10", time: "3:00 PM", available: false },
  { id: "slot-11", time: "3:30 PM", available: true },
  { id: "slot-12", time: "4:00 PM", available: true },
]

const quickActions = [
  {
    id: "hospital-visit",
    title: "Book Hospital",
    subtitle: "Visit",
    icon: Building2,
    href: "/book-appointment?type=consultation",
  },
  {
    id: "health-checkup",
    title: "Book Health",
    subtitle: "Checkup",
    icon: Stethoscope,
    href: "/book-appointment?type=test",
  },
  {
    id: "diagnostics",
    title: "Book",
    subtitle: "Scans",
    icon: ScanLine,
    href: "/diagnostics",
  },
]

type ViewState = "search" | "doctors" | "booking"

export default function BookNowPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [viewState, setViewState] = useState<ViewState>("search")
  const [visibleDoctors, setVisibleDoctors] = useState(6)
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "")
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  
  // Filter doctors and specialities for search dropdown suggestions (includes keyword matching)
  const searchSuggestedDoctors = searchQuery 
    ? doctorsData.filter(doc => {
        const query = searchQuery.toLowerCase()
        return (
          doc.name.toLowerCase().includes(query) ||
          doc.designation.toLowerCase().includes(query) ||
          doc.qualifications.toLowerCase().includes(query) ||
          doc.specialty.toLowerCase().includes(query) ||
          doc.keywords?.some(keyword => keyword.toLowerCase().includes(query))
        )
      })
    : []
  
  const searchSuggestedSpecialities = searchQuery
    ? specialities.filter(spec => {
        const query = searchQuery.toLowerCase()
        return (
          spec.name.toLowerCase().includes(query) ||
          spec.keywords?.some(keyword => keyword.toLowerCase().includes(query))
        )
      })
    : specialities
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get("location") || "")
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get("specialty") || "")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedDoctor, setSelectedDoctor] = useState<typeof doctorsData[0] | null>(null)
  const [selectedBranch, setSelectedBranch] = useState("")
  const [dateOffset, setDateOffset] = useState(0)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedSlot, setSelectedSlot] = useState("")
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false)
  const [showOtpPopup, setShowOtpPopup] = useState(false)
  const [showPaymentPopup, setShowPaymentPopup] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [mobileNumber, setMobileNumber] = useState("")
  const [otpValue, setOtpValue] = useState("")
  const [selectedPatientForPayment, setSelectedPatientForPayment] = useState<{id: number, name: string, uid: string} | null>(null)
  const [consultationType, setConsultationType] = useState<"hospital" | "video">("hospital")
  
  const [showPatientSelectPopup, setShowPatientSelectPopup] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<{id: number, name: string, uid: string, regDate: string} | null>(null)
  
  // Sample saved patients with more details
  const [savedPatients, setSavedPatients] = useState([
    { id: 1, name: "Nandini", uid: "MR2500032903", regDate: "10/11/2025" },
    { id: 2, name: "RAM", uid: "BCHTMP-022983", regDate: "15/01/2026" },
  ])
  
  // Form fields
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  })
  
  // Registration form fields
  const [registrationData, setRegistrationData] = useState({
    title: "Mr",
    patientName: "",
    contactNumber: "",
    emailId: "",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    motherName: "",
    gender: "Male",
    address: "",
    pincode: "",
    paymentType: "payNow",
    termsAccepted: false,
  })

  // Generate dates for the date picker
  const dates = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => addDays(new Date(), dateOffset + i))
  }, [dateOffset])

  // Filter doctors based on location and specialty
  const filteredDoctors = useMemo(() => {
    return doctorsData.filter((doctor) => {
      const matchesLocation = !selectedLocation || doctor.locations.includes(selectedLocation)
      const matchesSpecialty = !selectedSpecialty || doctor.specialtyId === selectedSpecialty
      const matchesSearch = !searchQuery || 
        doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesLocation && (matchesSpecialty || matchesSearch)
    })
  }, [selectedLocation, selectedSpecialty, searchQuery])

  useEffect(() => {
    const location = searchParams.get("location")
    const specialty = searchParams.get("specialty")
    const search = searchParams.get("search")
    const type = searchParams.get("type")
    const doctorSlug = searchParams.get("doctor")
    
    // Set consultation type based on URL
    if (window.location.pathname.includes("video-consultation")) {
      setConsultationType("video")
    } else if (type === "hospital") {
      setConsultationType("hospital")
    }
    
    if (location || specialty || search) {
      setViewState("doctors")
    }
    
    if (doctorSlug) {
      const doctor = doctorsData.find(d => d.slug === doctorSlug)
      if (doctor) {
        setSelectedDoctor(doctor)
        setSelectedBranch(doctor.locations[0])
        setViewState("booking")
      }
    }
  }, [searchParams])

  const handleBookAppointmentClick = () => {
    const params = new URLSearchParams()
    if (selectedLocation) params.append('location', selectedLocation)
    if (selectedSpecialty) params.append('specialty', selectedSpecialty)
    if (searchQuery) params.append('search', searchQuery)
    navigate(`/book-now${params.toString() ? '?' + params.toString() : ''}`)
    setViewState("doctors")
  }

  const handleSearch = () => {
    if (selectedLocation || searchQuery || selectedSpecialty) {
      setViewState("doctors")
    }
  }

  const handleSelectDoctor = (doctor: typeof doctorsData[0]) => {
    setSelectedDoctor(doctor)
    setSelectedBranch(doctor.locations[0])
    setConsultationType("hospital")
    setViewState("booking")
    navigate(`/hospital-visit?type=hospital&doctor=${doctor.slug}`)
  }

  const handleSelectDoctorForVideo = (doctor: typeof doctorsData[0]) => {
    setSelectedDoctor(doctor)
    setSelectedBranch(doctor.locations[0])
    setConsultationType("video")
    setViewState("booking")
    navigate(`/video-consultation?doctor=${doctor.slug}`)
  }

  const handleBackToDoctors = () => {
    setSelectedDoctor(null)
    setViewState("doctors")
  }

  const handleBackToSearch = () => {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    setViewState("search");
  }
};

  const handleSubmitCallback = (e: React.FormEvent) => {
    e.preventDefault()
    setShowThankYou(true)
  }

  const handleSlotSelect = (slotId: string) => {
    setSelectedSlot(slotId)
    setShowPhonePopup(true)
  }

  const handlePhoneConfirm = () => {
    if (!mobileNumber || mobileNumber.length < 10) {
      alert("Please enter a valid 10-digit mobile number")
      return
    }
    // Set the contact number in registration form and show OTP popup first
    setRegistrationData(prev => ({ ...prev, contactNumber: mobileNumber }))
    setShowPhonePopup(false)
    setShowOtpPopup(true)
  }

  const handleAddNewPatient = () => {
    setShowPatientSelectPopup(false)
    setShowRegistrationPopup(true)
  }

  const handleRegistrationSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!registrationData.termsAccepted) {
      alert("Please accept the terms and conditions")
      return
    }
    
    // Register new patient and go back to patient selection
    handleNewPatientRegistrationComplete()
  }

  const handleOtpConfirm = () => {
    if (!otpValue || otpValue.length < 4) {
      alert("Please enter a valid OTP")
      return
    }
    
    // After OTP verification, show patient selection popup
    setShowOtpPopup(false)
    setShowPatientSelectPopup(true)
    setOtpValue("")
  }

  const handleNewPatientRegistrationComplete = () => {
    // New patient - generate a new UID
    const newUid = `MR${String(Math.floor(Math.random() * 9000000000) + 1000000000)}`
    const today = new Date()
    const regDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`
    
    // Add the new patient to the saved patients list
    const newPatient = {
      id: savedPatients.length + 1,
      name: registrationData.patientName.toUpperCase(),
      uid: newUid,
      regDate: regDate
    }
    setSavedPatients(prev => [...prev, newPatient])
    setSelectedPatient(newPatient)
    
    // Close registration and go back to patient selection
    setShowRegistrationPopup(false)
    setShowPatientSelectPopup(true)
    
    // Reset registration form
    setRegistrationData({
      title: "Mr",
      patientName: "",
      contactNumber: "",
      emailId: "",
      dobDay: "",
      dobMonth: "",
      dobYear: "",
      motherName: "",
      gender: "Male",
      address: "",
      pincode: "",
      paymentType: "payNow",
      termsAccepted: false,
    })
  }

  const handlePayNow = () => {
    if (!selectedPatient) {
      alert("Please select a patient first")
      return
    }
    setSelectedPatientForPayment({
      id: selectedPatient.id,
      name: selectedPatient.name,
      uid: selectedPatient.uid
    })
    setShowPatientSelectPopup(false)
    setShowPaymentPopup(true)
  }

  const handlePaymentComplete = () => {
    setShowPaymentPopup(false)
    setShowThankYou(true)
  }

  const handlePayAtHospital = () => {
    if (!selectedPatient) {
      alert("Please select a patient first")
      return
    }
    setSelectedPatientForPayment({
      id: selectedPatient.id,
      name: selectedPatient.name,
      uid: selectedPatient.uid
    })
    setShowPatientSelectPopup(false)
    setShowThankYou(true)
  }

  const getLocationName = (id: string) => {
    return locations.find(l => l.id === id)?.name || id
  }

  // Thank You View - Check this FIRST before other views
  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-8 px-4">
        <div className="max-w-3xl w-full mx-auto">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Appointment Confirmed!</h1>
            <p className="text-gray-600">Your appointment has been successfully booked</p>
          </div>

          {/* Appointment Details Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b">Appointment Details</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient Name</span>
                <span className="font-semibold text-gray-900 text-right">{selectedPatient?.name || selectedPatientForPayment?.name}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient ID</span>
                <span className="font-semibold text-primary text-right">{selectedPatient?.uid || selectedPatientForPayment?.uid}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Doctor</span>
                <span className="font-semibold text-gray-900 text-right">{selectedDoctor?.name}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Speciality</span>
                <span className="font-semibold text-gray-900 text-right">{selectedDoctor?.specialty}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Consultation Type</span>
                <span className="font-semibold text-gray-900 text-right">{consultationType === "video" ? "Video Consultation" : "Hospital Visit"}</span>
              </div>
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Date</span>
                <span className="font-semibold text-gray-900 text-right">{selectedDate && format(selectedDate, "dd MMM yyyy")}</span>
              </div>
              
              {consultationType === "hospital" && selectedSlot && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Time</span>
                  <span className="font-semibold text-gray-900 text-right">{timeSlots.find(s => s.id === selectedSlot)?.time}</span>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Location</span>
                <span className="font-semibold text-gray-900 text-right">{getLocationName(selectedBranch)}</span>
              </div>
            </div>
          </div>

          {/* Important Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Important Instructions
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Please arrive 15 minutes before your scheduled appointment time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Carry your Patient ID card and any previous medical records</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Wear a mask and maintain social distancing at the hospital</span>
              </li>
              {consultationType === "video" && (
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Ensure stable internet connection and a quiet environment for video consultation</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>For any queries, contact us at +91 99660 60000</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              size="lg" 
              className="flex-1"
              onClick={() => {
                setShowThankYou(false)
                setViewState("search")
                setSelectedDoctor(null)
                setSelectedPatient(null)
                setSelectedPatientForPayment(null)
                setSelectedDate(null)
                setSelectedSlot("")
                navigate("/book-now")
              }}
            >
              Book Another Appointment
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="flex-1"
              onClick={() => navigate("/")}
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Search View (Hero)
  if (viewState === "search") {
    return (
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/happy-mother-child.png"
              alt="Pranaam Hospitals"
              
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>

          <div className="relative z-10 container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-2">
             Leading Hospital
            </h1>
            <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-8">
              for Quality Healthcare in Hyderabad<span className="text-primary">*</span>
            </p>

            {/* Search Bar - Location Left, Search Right */}
            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl p-2 flex flex-col md:flex-row gap-2">
              {/* Location Dropdown - LEFT */}
              <div className="flex items-center gap-2 px-4 py-1 min-w-[160px] border-b md:border-b-0 md:border-r border-gray-200">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <Select value={selectedLocation || "all"} onValueChange={(val) => setSelectedLocation(val === "all" ? "" : val)}>
                  <SelectTrigger className="border-0 shadow-none focus:ring-0 text-base p-0 h-auto">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search Input with Dropdown - RIGHT */}
              <div className="flex-1 relative">
                <div className="flex items-center gap-2 px-4 py-2">
                  <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search for Doctor or Speciality"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setShowSearchDropdown(true)
                    }}
                    onFocus={() => setShowSearchDropdown(true)}
                    className="flex-1 border-0 outline-none text-base bg-transparent placeholder:text-gray-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery("")
                        setSelectedSpecialty("")
                        setShowSearchDropdown(false)
                      }}
                      className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                </div>

                {/* Search Dropdown */}
                {showSearchDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border max-h-80 overflow-y-auto z-50">
                    {/* Filtered Specialities */}
                    {searchSuggestedSpecialities.length > 0 && (
                      <div>
                        <p className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50">Specialities</p>
                        {searchSuggestedSpecialities.slice(0, 5).map((spec) => (
                          <button
                            key={spec.id}
                            onClick={() => {
                              setSelectedSpecialty(spec.id)
                              setSearchQuery(spec.name)
                              setShowSearchDropdown(false)
                            }}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Stethoscope className="w-5 h-5 text-primary" />
                            </div>
                            <span className="font-medium text-gray-800">{spec.name}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Filtered Doctors */}
                    {searchSuggestedDoctors.length > 0 && (
                      <div>
                        <p className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50">Doctors</p>
                        {searchSuggestedDoctors.slice(0, 5).map((doc) => (
                          <button
                            key={doc.id}
                            onClick={() => {
                              setSelectedDoctor(doc)
                              setViewState("booking")
                              setShowSearchDropdown(false)
                            }}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                              <img
                                src={doc.image}
                                alt={doc.name}
                                width={40}
                                height={40}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{doc.name}</p>
                              <p className="text-sm text-gray-500">{doc.designation}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No Results */}
                    {searchQuery && searchSuggestedDoctors.length === 0 && searchSuggestedSpecialities.length === 0 && (
                      <p className="px-4 py-4 text-gray-500 text-center">No results found</p>
                    )}
                  </div>
                )}
              </div>

              <Button
                size="lg"
                className="px-6 md:px-8 py-4 md:py-6 text-sm md:text-base font-semibold whitespace-nowrap"
                onClick={handleBookAppointmentClick}
              >
                Book an Appointment
              </Button>
            </div>

            {/* Click outside to close dropdown */}
            {showSearchDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowSearchDropdown(false)}
              />
            )}

            {/* Quick Actions - Responsive Grid */}
            <div className="max-w-4xl mx-auto mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 md:px-0">
              {quickActions.map((action) => (
                <Link
                  key={action.id}
                  href={action.href}
                  className="group bg-white/95 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center sm:mb-3 group-hover:scale-110 transition-transform flex-shrink-0">
                      <action.icon className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{action.title}</h3>
                      <p className="text-gray-600 text-sm">{action.subtitle}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <p className="text-white/80 text-sm mt-6">As per Outlook Health Awards 2023*</p>
          </div>
        </section>

        {/* Doctors List Section */}
        <section className="py-8 md:py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 md:mb-8">
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  {selectedSpecialty ? `${specialities.find(s => s.id === selectedSpecialty)?.name || 'Our'} Doctors` : 'Our Doctors'}
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  {doctorsData.filter(doc => !selectedSpecialty || doc.specialtyId === selectedSpecialty).length} Doctors Found
                </p>
              </div>

              {/* Doctors Grid - Responsive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
                {doctorsData
                  .filter(doc => !selectedSpecialty || doc.specialtyId === selectedSpecialty)
                  .slice(0, visibleDoctors)
                  .map((doctor) => (
                    <div 
                      key={doctor.id}
                      className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow p-5"
                    >
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                          <img
                            src={doctor.image}
                            alt={doctor.name}
                            width={80}
                            height={80}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 text-lg">{doctor.name}</h3>
                          <p className="text-primary text-sm font-medium">{doctor.designation}</p>
                          <p className="text-gray-500 text-sm">{doctor.qualifications}</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          <span>{doctor.languages.join(", ")}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{doctor.locations.map(loc => locations.find(l => l.id === loc)?.name).join(", ")}</span>
                      </div>

                      <Button 
                        className="w-full mt-4"
                        onClick={() => {
                          setSelectedDoctor(doctor)
                          setViewState("booking")
                        }}
                      >
                        Book Appointment
                      </Button>
                    </div>
                  ))}
              </div>

              {/* Load More Button */}
              {visibleDoctors < doctorsData.filter(doc => !selectedSpecialty || doc.specialtyId === selectedSpecialty).length && (
                <div className="text-center">
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => setVisibleDoctors(prev => prev + 6)}
                    className="px-8"
                  >
                    Load More Doctors
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Additional Info Sections */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-primary text-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <Phone className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">24/7 Emergency</h3>
                    <p className="text-white/80">Always here for you</p>
                  </div>
                </div>
                <p className="text-3xl font-bold mb-4">+91 99660 60000</p>
                <Button variant="secondary" className="w-full" asChild>
                  <a href="tel:+919966060000">Call Now</a>
                </Button>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Book Appointment</h3>
                    <p className="text-gray-600">Schedule your visit</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">Book appointments with our expert doctors.</p>
                <Button className="w-full" asChild>
                  <Link href="/book-appointment">
                    Book Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <Clock className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Working Hours</h3>
                    <p className="text-gray-600">OPD Timings</p>
                  </div>
                </div>
                <div className="space-y-2 text-gray-600">
                  <p className="flex justify-between">
                    <span>Mon - Sat</span>
                    <span className="font-medium text-gray-900">9 AM - 8 PM</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-medium text-gray-900">10 AM - 2 PM</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Emergency</span>
                    <span className="font-medium text-primary">24/7</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // Doctors List View
  if (viewState === "doctors") {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBackToSearch}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900">Find a Doctor</h1>
                <p className="text-sm text-gray-600">
                  {filteredDoctors.length} doctors found
                  {selectedLocation && ` in ${getLocationName(selectedLocation)}`}
                  {searchQuery && ` for "${searchQuery}"`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="space-y-4">
                {/* Center of Excellence */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
                    Centre of Excellence
                    <button className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </h3>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search"
                      className="pl-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4" />
                      <span className="text-sm">Child Care</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4" />
                      <span className="text-sm">Women Care</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4" />
                      <span className="text-sm">Fertility Care</span>
                    </label>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
                    Location
                    <button className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </h3>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search for Location"
                      className="pl-10"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="location"
                        checked={!selectedLocation}
                        onChange={() => setSelectedLocation("")}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-gray-700">All Locations</span>
                    </label>
                    {locations.map((location) => (
                      <label key={location.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="location"
                          checked={selectedLocation === location.id}
                          onChange={() => setSelectedLocation(location.id)}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Department */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
                    Department
                    <button className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </h3>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search"
                      className="pl-10"
                    />
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="department"
                        checked={!selectedSpecialty}
                        onChange={() => setSelectedSpecialty("")}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-gray-700">All Departments</span>
                    </label>
                    {departments.map((dept) => (
                      <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="department"
                          checked={selectedSpecialty === dept.id}
                          onChange={() => setSelectedSpecialty(dept.id)}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="text-sm text-gray-700">{dept.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {filteredDoctors.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Doctors Found</h3>
                  <p className="text-gray-600 mb-4">Try adjusting your filters or search criteria.</p>
                  <Button onClick={handleBackToSearch}>Search Again</Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {filteredDoctors.map((doctor) => (
                    <div
                      key={doctor.id}
                      className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition-shadow cursor-pointer"
                      // onClick={() => handleSelectDoctor(doctor)}
                    >
                      <div className="p-6">
                        <div className="flex gap-4">
                          <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                            <img
                              src={doctor.image}
                              alt={doctor.name}
                              
                              className="object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-lg text-primary truncate">{doctor.name}</h3>
                            <p className="text-gray-600 text-sm">{doctor.designation}</p>
                            <p className="text-gray-500 text-sm mt-1">{doctor.qualifications}</p>
                            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                              <Globe className="w-4 h-4" /> 
                              {doctor.languages.join(", ")}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                            <MapPin className="w-4 h-4" />
                            {doctor.locations.map(l => getLocationName(l)).join(", ")}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="flex-1"
                              onClick={() => handleSelectDoctor(doctor)}
                            >
                              Book Now
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1 border-primary text-primary hover:bg-primary hover:text-white"
                              onClick={() => handleSelectDoctorForVideo(doctor)}
                            >
                              Video Consultation
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Booking View (Doctor Detail with Slots)
  if (viewState === "booking" && selectedDoctor) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <Button variant="ghost" size="sm" onClick={handleBackToDoctors}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Doctors
            </Button>
          </div>
        </div>

        <div className="container mx-auto px-4 py-4 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            {/* Left Column - Doctor Info & Branch Selection */}
            <div className="space-y-4 md:space-y-6">
              {/* Doctor Card */}
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mx-auto sm:mx-0">
                    <img
                      src={selectedDoctor.image}
                      alt={selectedDoctor.name}
                      
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedDoctor.name}</h2>
                    <p className="text-primary font-medium text-sm sm:text-base">{selectedDoctor.designation}</p>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1">{selectedDoctor.qualifications}</p>
                    
                    <div className="mt-3 sm:mt-4 space-y-2">
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm text-gray-600">
                        <Globe className="w-4 h-4" />
                        {selectedDoctor.languages.join(", ")}
                      </div>
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {selectedDoctor.locations.map(l => getLocationName(l)).join(", ")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Book Appointment Selection */}
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">Book an appointment</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Branch</label>
                    <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedDoctor.locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>
                            {getLocationName(loc)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Speciality</label>
                    <Select value={selectedDoctor.specialtyId} disabled>
                      <SelectTrigger>
                        <SelectValue>{selectedDoctor.specialty}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={selectedDoctor.specialtyId}>
                          {selectedDoctor.specialty}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                  <Button
                    className="flex-1"
                    variant={consultationType === "hospital" ? "default" : "outline"}
                   onClick={() => {
                      setConsultationType("hospital")
                      setSelectedDate(null)
                      setSelectedSlot("")

                      if (selectedDoctor) {
                        navigate(`/hospital-visit?doctor=${selectedDoctor.slug}`)
                      }
                    }}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Hospital Visit
                  </Button>
                  <Button
                    className="flex-1"
                    variant={consultationType === "video" ? "default" : "outline"}
                   onClick={() => {
                      setConsultationType("video")
                      setSelectedDate(null)
                      setSelectedSlot("")

                      if (selectedDoctor) {
                        navigate(`/video-consultation?doctor=${selectedDoctor.slug}`)
                      }
                    }}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Video Consultation
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column - Date Selection & Form */}
            <div className="space-y-4 md:space-y-6">
              {/* Available Slots */}
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">Available slots</h3>
                
                {/* Date Picker */}
                <div className="flex items-center gap-1 md:gap-2 mb-4 md:mb-6 pb-3 md:pb-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDateOffset(Math.max(0, dateOffset - 1))}
                    disabled={dateOffset === 0}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  
                  <div className="flex-1 flex gap-1 md:gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {dates.map((date, index) => {
                      const isSelected = selectedDate?.toDateString() === date.toDateString()
                      const isToday = date.toDateString() === new Date().toDateString()
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedDate(date)}
                          className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                            isSelected
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {isToday ? "Today" : format(date, "EEE")}, {format(date, "dd MMM")}
                        </button>
                      )
                    })}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDateOffset(dateOffset + 1)}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Time Slots */}
                {selectedDate ? (
                  consultationType === "video" ? (
                    // For video consultation, just show a message
                    <div className="text-center py-6 md:py-8 mb-4 md:mb-6">
                      <p className="text-sm md:text-base text-gray-700 font-medium mb-2">Video Consultation Selected</p>
                      <p className="text-xs md:text-sm text-gray-600">Please proceed to confirm your appointment</p>
                      <Button 
                        onClick={() => setShowPhonePopup(true)}
                        className="mt-4"
                      >
                        Continue
                      </Button>
                    </div>
                  ) : (
                    // For hospital visit, show time slots
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4 md:mb-6 pb-6 md:pb-10">
                      {timeSlots.filter(s => s.available).map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => handleSlotSelect(slot.id)}
                          className={`px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                            selectedSlot === slot.id
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4 mb-4 md:mb-6">
                    Please select a date to view available slots
                  </p>
                )}

                {/* Selected slot info - Show when a slot is selected and slots ARE available */}
                {selectedDate && consultationType === "hospital" && selectedSlot && timeSlots.some(s => s.available) && (
                  <div className="border-t pt-4 md:pt-6">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                      <p className="text-xs md:text-sm text-gray-600">
                        Selected: <strong className="text-primary">{format(selectedDate, "dd MMM yyyy")}</strong> at <strong className="text-primary">{timeSlots.find(s => s.id === selectedSlot)?.time}</strong>
                      </p>
                      <div className="text-left sm:text-right">
                        <p className="text-xs md:text-sm text-gray-600">For walk-in Accommodation</p>
                        <p className="text-primary font-bold text-base md:text-lg">1800 2122</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Callback Form - ONLY show when NO slots are available */}
                {!timeSlots.some(s => s.available) && (
                  <div className="border-t pt-4 md:pt-6">
                    <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
                      <div className="flex-1">
                        <p className="text-xs md:text-sm text-gray-600 mb-4">
                          No slots available for this date.  out the form below to request a callback.
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-xs md:text-sm text-gray-600">For walk-in Accommodation</p>
                        <p className="text-primary font-bold text-base md:text-lg">1800 2122</p>
                      </div>
                    </div>

                    <form onSubmit={handleSubmitCallback} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Enter Your Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Id <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          placeholder="Enter Email Id"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Number <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="tel"
                          placeholder="Enter Contact Number"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        Submit
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Phone Number Confirmation Popup */}
        <Dialog open={showPhonePopup} onOpenChange={setShowPhonePopup}>
          <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
            <div className="relative">
              <button
                onClick={() => setShowPhonePopup(false)}
                className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm your appointment</h2>
                
                <div className="mb-2">
                  <p className="text-gray-700">
                    {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                    <span className="text-primary font-semibold">{selectedDoctor.name}</span>
                  </p>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <p className="text-gray-700">
                    {selectedDate && format(selectedDate, "dd, MMM yyyy")}
                  </p>
                  <p className="text-gray-700">
                    {timeSlots.find(s => s.id === selectedSlot)?.time}
                  </p>
                </div>

                <p className="text-gray-600 text-sm mb-4">
                  Please enter the registered mobile number for the specific patient.
                </p>

                <div className="flex gap-3 items-center">
                  {/* Combined Flag + Input Field */}
                  <div className="flex items-center flex-1 border rounded-md overflow-hidden h-11">
                    <div className="flex items-center gap-2 px-3 bg-gray-50 h-full border-r">
                      <img
                        src="https://flagcdn.com/w20/in.png"
                        alt="India"
                        width={20}
                        height={15}
                        className="rounded-sm"
                      />
                      <span className="text-gray-600 text-sm font-medium">+91</span>
                    </div>
                    <Input
                      type="tel"
                      placeholder="Enter Mobile Number"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0"
                      maxLength={10}
                    />
                  </div>
                  <Button 
                    onClick={handlePhoneConfirm}
                    className="px-6 h-11 whitespace-nowrap"
                  >
                    Confirm Booking
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Step 2: Patient Selection Popup with Table Design */}
        <Dialog open={showPatientSelectPopup} onOpenChange={setShowPatientSelectPopup}>
          <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden [&>button]:hidden">
            <div className="relative">
              <button
                onClick={() => setShowPatientSelectPopup(false)}
                className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm your appointment</h2>
                
                <div className="mb-2">
                  <p className="text-gray-700">
                    {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                    <span className="text-primary font-semibold uppercase">{selectedDoctor.name.toUpperCase()}</span>
                  </p>
                </div>

                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-700">
                    {selectedDate && format(selectedDate, "dd, MMM yyyy")}
                  </p>
                  <p className="text-gray-700">
                    {timeSlots.find(s => s.id === selectedSlot)?.time}
                  </p>
                </div>

                {/* Payable Amount */}
                <p className="text-rose-500 font-medium mb-4">
                  Payable Amount: <span className="text-rose-500">Registration: 210/-, Consultation: 900/-</span>
                </p>

                <p className="text-gray-600 text-sm mb-4">
                  Please select the ID or add the new patient to proceed.
                </p>

                {/* Patient Table with Radio Selection */}
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium w-16">Select</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">SNo.</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Patient Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">UID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedPatients.map((patient, index) => (
                        <tr 
                          key={patient.id} 
                          className={`border-t cursor-pointer hover:bg-gray-50 ${selectedPatient?.id === patient.id ? 'bg-primary/5' : ''}`}
                          onClick={() => setSelectedPatient(patient)}
                        >
                          <td className="px-4 py-3">
                            <input 
                              type="radio" 
                              name="patientSelect" 
                              checked={selectedPatient?.id === patient.id}
                              onChange={() => setSelectedPatient(patient)}
                              className="w-4 h-4 text-primary accent-primary"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-primary">{index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium">{patient.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{patient.uid}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <Button 
                    onClick={handleAddNewPatient}
                    className="px-4"
                  >
                    Add a new patient
                  </Button>

                  {selectedPatient && (
                    <div className="flex gap-2">
                      <Button 
                        onClick={handlePayNow}
                        className="px-6"
                      >
                        Pay Now
                      </Button>
                      <Button 
                        variant="outline"
                        className="px-4 border-primary text-primary hover:bg-primary/5"
                        onClick={handlePayAtHospital}
                      >
                        Pay at Hospital
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Step 3: OTP Verification Popup */}
        <Dialog open={showOtpPopup} onOpenChange={setShowOtpPopup}>
          <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
            <div className="relative">
              <button
                onClick={() => setShowOtpPopup(false)}
                className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm OTP</h2>
                
                <div className="mb-2">
                  <p className="text-gray-700">
                    {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                    <span className="text-primary font-semibold">{selectedDoctor.name}</span>
                  </p>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <p className="text-gray-700">
                    {selectedDate && format(selectedDate, "dd, MMM yyyy")}
                  </p>
                  <p className="text-gray-700">
                    {timeSlots.find(s => s.id === selectedSlot)?.time}
                  </p>
                </div>

                <p className="text-gray-600 text-sm mb-4">
                  We have sent you an OTP on {mobileNumber}.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Enter OTP<span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder="Please Enter OTP"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="flex-1"
                      maxLength={6}
                    />
                    <Button 
                      onClick={handleOtpConfirm}
                      className="px-6"
                    >
                      Confirm OTP
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-sm">Still not received OTP?</span>
                  <Button 
                    variant="default"
                    size="sm"
                    onClick={() => alert("OTP resent to " + mobileNumber)}
                  >
                    Resend
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Step 4: Payment Popup */}
        <Dialog open={showPaymentPopup} onOpenChange={setShowPaymentPopup}>
          <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
            <div className="relative">
              <button
                onClick={() => setShowPaymentPopup(false)}
                className="absolute right-2 top-2 w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="p-8">
                {/* Patient Details - Clean row layout */}
                <div className="space-y-3 pb-4 border-b">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Patient Name</span>
                    <span className="font-semibold text-gray-900">{selectedPatientForPayment?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">UID</span>
                    <span className="font-semibold text-gray-900">{selectedPatientForPayment?.uid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Doctor</span>
                    <span className="font-semibold text-gray-900">{selectedDoctor.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date</span>
                    <span className="font-semibold text-gray-900">{selectedDate && format(selectedDate, "dd MMM yyyy")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time</span>
                    <span className="font-semibold text-gray-900">{timeSlots.find(s => s.id === selectedSlot)?.time}</span>
                  </div>
                </div>

                {/* Fee Breakdown */}
                <div className="py-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Registration Fee</span>
                    <span className="font-semibold text-gray-900">Rs. 210/-</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Consultation Fee</span>
                    <span className="font-semibold text-gray-900">Rs. 900/-</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-semibold text-primary">Total Amount</span>
                    <span className="font-bold text-primary">Rs. 1,110/-</span>
                  </div>
                </div>

                {/* Payment Method Selection */}
                {/* <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700">Select Payment Method</p>
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input 
                      type="radio" 
                      name="payment" 
                      value="upi" 
                      className="w-5 h-5 accent-primary" 
                      defaultChecked 
                    />
                    <span className="font-medium text-gray-800">UPI (GPay, PhonePe, Paytm)</span>
                  </label>
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input 
                      type="radio" 
                      name="payment" 
                      value="card" 
                      className="w-5 h-5 accent-primary" 
                    />
                    <span className="font-medium text-gray-800">Credit / Debit Card</span>
                  </label>
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input 
                      type="radio" 
                      name="payment" 
                      value="netbanking" 
                      className="w-5 h-5 accent-primary" 
                    />
                    <span className="font-medium text-gray-800">Net Banking</span>
                  </label>
                </div> */}

                <Button onClick={handlePaymentComplete} className="w-full py-6 text-lg">
                  Pay Rs. 1,110/-
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Registration Popup */}
        <Dialog open={showRegistrationPopup} onOpenChange={setShowRegistrationPopup}>
          <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
            <div className="relative">
              <button
                onClick={() => setShowRegistrationPopup(false)}
                className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Register</h2>
                <p className="text-gray-600 mb-6">Register yourself to make an appointment with the professional.</p>
                
                <form onSubmit={handleRegistrationSubmit} className="space-y-4">
                  {/* Patient Name & Contact */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Patient / Visitor Name<span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <Select 
                          value={registrationData.title} 
                          onValueChange={(val) => setRegistrationData(prev => ({ ...prev, title: val }))}
                        >
                          <SelectTrigger className="w-[80px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mr">Mr</SelectItem>
                            <SelectItem value="Mrs">Mrs</SelectItem>
                            <SelectItem value="Ms">Ms</SelectItem>
                            <SelectItem value="Dr">Dr</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Enter Patient / Visitor Name"
                          value={registrationData.patientName}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, patientName: e.target.value }))}
                          className="flex-1"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Mobile Number<span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="tel"
                        placeholder="Enter Mobile Number"
                        value={registrationData.contactNumber}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, contactNumber: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                        maxLength={10}
                        required
                      />
                    </div>
                  </div>

                  {/* Email & DOB */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email ID<span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="Enter Email ID"
                        value={registrationData.emailId}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, emailId: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth<span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <Select 
                          value={registrationData.dobDay} 
                          onValueChange={(val) => setRegistrationData(prev => ({ ...prev, dobDay: val }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Day" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 31 }, (_, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={registrationData.dobMonth} 
                          onValueChange={(val) => setRegistrationData(prev => ({ ...prev, dobMonth: val }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Month" />
                          </SelectTrigger>
                          <SelectContent>
                            {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((month, i) => (
                              <SelectItem key={month} value={String(i + 1)}>{month}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={registrationData.dobYear} 
                          onValueChange={(val) => setRegistrationData(prev => ({ ...prev, dobYear: val }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 100 }, (_, i) => (
                              <SelectItem key={2026 - i} value={String(2026 - i)}>{2026 - i}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Mother Name & Gender */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mother Name<span className="text-red-500">*</span>
                      </label>
                      <Input
                        placeholder="Enter Mother Name"
                        value={registrationData.motherName}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, motherName: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender<span className="text-red-500">*</span>
                      </label>
                      <Select 
                        value={registrationData.gender} 
                        onValueChange={(val) => setRegistrationData(prev => ({ ...prev, gender: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Address & Pincode */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address<span className="text-red-500">*</span>
                      </label>
                      <Input
                        placeholder="Address"
                        value={registrationData.address}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, address: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode<span className="text-red-500">*</span>
                      </label>
                      <Input
                        placeholder="Pincode"
                        value={registrationData.pincode}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        maxLength={6}
                        required
                      />
                    </div>
                  </div>

                  {/* Payment Type */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Type<span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentType"
                          value="payNow"
                          checked={registrationData.paymentType === "payNow"}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, paymentType: e.target.value }))}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="text-gray-700">Pay Now</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentType"
                          value="payAtHospital"
                          checked={registrationData.paymentType === "payAtHospital"}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, paymentType: e.target.value }))}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="text-gray-700">Pay at Hospital</span>
                      </label>
                    </div>
                  </div> */}

                  {/* Terms */}
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={registrationData.termsAccepted}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                      className="mt-1 w-4 h-4"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      I understand, accept and agree to the terms and conditions. I hereby authorize Pranaam Hospitals and their authorized partners to contact me through email and SMS or any other modes of communication. It will override my registry on NCPR.
                    </label>
                  </div>

                  <Button type="submit" className="w-full">
                    Submit
                  </Button>
                </form>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  return null
}
